import { notifyUnauthorized, type PanelApi } from '../application/panelApi';
import type { ControlBuild } from '../domain/nodeBuild';
import type { AutopilotState, Cluster, PanelRole, PanelUser, WarmupJob } from '../domain/panel';
import { createBrowserRequest } from './browserRequest';

type AutopilotResponse = { autopilot?: AutopilotState; pipeline?: AutopilotState };
type WarmupPoolResponse = { remaining?: number };

const requestPort = createBrowserRequest(notifyUnauthorized);

const enc = encodeURIComponent;
const get = (p: string) => requestPort(p);
const post = (p: string, body?: unknown) =>
  requestPort(p, { method: 'POST', body: body === undefined ? {} : body });
const put = (p: string, body: unknown) => requestPort(p, { method: 'PUT', body });
const del = (p: string) => requestPort(p, { method: 'DELETE' });

export const BrowserPanelApi = {
  auth: {
    me: () =>
      get('/api/auth/me') as Promise<{
        username?: string;
        displayName?: string;
        role?: string;
        permissions?: string[];
        userId?: string;
      }>,
    login: (creds: { username: string; password: string }) =>
      post('/api/auth/login', creds) as Promise<{
        username?: string;
        displayName?: string;
        role?: string;
        permissions?: string[];
        userId?: string;
      }>,
    logout: () => post('/api/auth/logout'),
  },
  users: {
    list: () => get('/api/users') as Promise<{ users: PanelUser[] }>,
    create: (body: unknown) => post('/api/users', body) as Promise<{ user: PanelUser; password?: string }>,
    update: (id: string, body: unknown) =>
      put(`/api/users/${enc(id)}`, body) as Promise<{ user: PanelUser; password?: string }>,
    remove: (id: string) => del(`/api/users/${enc(id)}`) as Promise<{ ok: boolean }>,
  },
  roles: {
    list: () => get('/api/roles') as Promise<{ roles: PanelRole[] }>,
    create: (body: unknown) => post('/api/roles', body) as Promise<{ role: PanelRole }>,
    update: (id: string, body: unknown) => put(`/api/roles/${enc(id)}`, body) as Promise<{ role: PanelRole }>,
    remove: (id: string) => del(`/api/roles/${enc(id)}`) as Promise<{ ok: boolean }>,
  },
  tasks: {
    list: () => get('/api/tasks'),
    chart: () => get('/api/tasks/chart') as Promise<{ chart?: unknown }>,
    create: (payload: unknown) => post('/api/tasks', payload) as Promise<{ task?: { id?: string } }>,
    update: (id: string, payload: unknown) => put(`/api/tasks/${enc(id)}`, payload),
    remove: (id: string) => del(`/api/tasks/${enc(id)}`),
    setStatus: (id: string, action: string) => post(`/api/tasks/${enc(id)}/${action}`),
    bulk: (ids: string[], action: string) => post('/api/tasks/bulk', { ids, action }),
  },
  scenarios: {
    list: () => get('/api/scenarios'),
    create: (input: unknown) => post('/api/scenarios', input),
    update: (id: string, input: unknown) => put(`/api/scenarios/${enc(id)}`, input),
    remove: (id: string) => del(`/api/scenarios/${enc(id)}`),
  },
  proxyPools: {
    list: () => get('/api/proxy-pools'),
    upload: (payload: unknown) => post('/api/proxy-pools/upload', payload),
    update: (id: string, body: unknown) => put(`/api/proxy-pools/${enc(id)}`, body),
    remove: (id: string) => del(`/api/proxy-pools/${enc(id)}`),
    clear: () => del('/api/proxy-pools'),
  },
  proxies: {
    parse: (content: string, protocol?: string) => post('/api/proxies/parse', { content, protocol }),
    list: () => get('/api/proxies'),
    remove: (indexes: number[]) => post('/api/proxies/remove', { indexes }),
  },
  logs: {
    report: (params: string) => get(`/api/logs?${params}`),
    query: (params?: URLSearchParams | string) => get(params ? `/api/logs?${params}` : '/api/logs'),
  },
  settings: {
    get: () => get('/api/settings'),
    save: (payload: unknown) => put('/api/settings', payload),
    stopAll: () => post('/api/settings/stop-all'),
    resetStats: () => post('/api/settings/reset-stats') as Promise<{ reset?: number; failed?: string[] }>,
    telegramTest: () => post('/api/settings/telegram-test') as Promise<{ chatId?: string }>,
    password: (body: { current: string; next: string }) =>
      post('/api/settings/password', body) as Promise<{ ephemeral?: boolean }>,
  },
  clusters: {
    list: () => get('/api/clusters') as Promise<{ clusters?: Cluster[]; control?: ControlBuild }>,
    create: (body: unknown) =>
      post('/api/clusters', body) as Promise<{ deploying?: boolean; hasSshPassword?: boolean }>,
    update: (id: string, body: unknown) => put(`/api/clusters/${enc(id)}`, body),
    remove: (id: string) => del(`/api/clusters/${enc(id)}`),
    ping: (id: string) => post(`/api/clusters/${enc(id)}/ping`),
    pingAll: () =>
      post('/api/clusters/ping-all') as Promise<{ results?: Array<{ online?: boolean; deploying?: boolean }> }>,
    deploy: (id: string) => post(`/api/clusters/${enc(id)}/deploy`),
    profiles: (id: string) => get(`/api/clusters/${enc(id)}/profiles`),
    profileList: (id: string) => get(`/api/clusters/${enc(id)}/profile-list`),
    profileLog: (id: string, profile: string) =>
      get(`/api/clusters/${enc(id)}/profile-log?profile=${encodeURIComponent(profile)}`),
    profilePersona: (id: string, profile: string) =>
      get(`/api/clusters/${enc(id)}/profile-persona?profile=${enc(profile)}`),
    fpTest: (id: string, run?: string) =>
      get(`/api/clusters/${enc(id)}/fp-test${run ? `?id=${enc(run)}` : ''}`),
    fpTestStart: (id: string, body: unknown) => post(`/api/clusters/${enc(id)}/fp-test`, body),
    fpShot: (id: string, run: string, file: string) =>
      get(`/api/clusters/${enc(id)}/fp-shot?run=${enc(run)}&file=${enc(file)}`),
    profilesDelete: (id: string, filter: unknown, dryRun: boolean) =>
      post(`/api/clusters/${enc(id)}/profiles-delete`, { filter, dryRun }),
    profilesDeleteState: (id: string) => get(`/api/clusters/${enc(id)}/profiles-delete`),
  },
  profileTemplate: {
    get: () => get('/api/profile-template'),
    save: (template: unknown) => put('/api/profile-template', { template }),
  },
  fpLibrary: {
    list: () => get('/api/fp-library'),
    add: (items: Array<{ name: string; data: string }>) => post('/api/fp-library', { items }),
    importPublic: () => post('/api/fp-library/import-public', {}),
    remove: (id: string) => del(`/api/fp-library/${enc(id)}`),
  },
  warmup: {
    jobs: () => get('/api/warmup/jobs') as Promise<{ jobs?: WarmupJob[] }>,
    start: (body: unknown, isControl: boolean) =>
      post(isControl ? '/api/warmup' : '/api/farm/warmup', body) as Promise<{
        pooled?: number;
        remaining?: number;
        jobs?: WarmupJob[];
        jobsEnqueued?: number;
      }>,
    pool: () => get('/api/warmup/pool') as Promise<WarmupPoolResponse>,
    clearPool: () => post('/api/warmup/pool/clear', {}) as Promise<WarmupPoolResponse>,
  },
  autopilot: {
    get: () => get('/api/autopilot'),
    save: (body: unknown) => put('/api/autopilot', body) as Promise<AutopilotResponse>,
    start: (body?: unknown) => post('/api/autopilot/start', body || {}) as Promise<AutopilotResponse>,
    stop: () => post('/api/autopilot/stop', {}) as Promise<AutopilotResponse>,
  },
  clickTest: {
    run: (payload: unknown) => post('/api/click-test', payload),
  },
  clickCookies: {
    summary: () => get('/api/click-cookies') as Promise<{ unused: number; used: number }>,
    upload: (
      body: { name: string; content: string } | { items: Array<{ name: string; content: string }> },
    ) =>
      post('/api/click-cookies', body) as Promise<{
        id?: string;
        cookies?: number;
        added?: number;
        skipped?: number;
        unused: number;
        used: number;
      }>,
  },
  ranks: {
    list: (params?: { days?: number; taskId?: string; engine?: string }) => {
      const q = new URLSearchParams();
      if (params?.days) q.set('days', String(params.days));
      if (params?.taskId && params.taskId !== 'all') q.set('taskId', params.taskId);
      if (params?.engine) q.set('engine', params.engine);
      const suffix = q.toString();
      return get(suffix ? `/api/ranks?${suffix}` : '/api/ranks') as Promise<import('../domain/ranks').RankMatrix>;
    },
    probe: () => post('/api/ranks/probe') as Promise<{ ok?: boolean; probe?: import('../domain/ranks').RankProbeInfo }>,
  },
  gallery: {
    list: () => get('/api/gallery'),
    upload: (body: unknown) => post('/api/gallery', body),
    remove: (name: string) => del(`/api/gallery/${enc(name)}`),
  },
} satisfies PanelApi;
